import _objectSpread from "@babel/runtime/helpers/esm/objectSpread";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

/**
 * External dependencies
 */
import { findKey } from 'lodash';
/**
 * WordPress dependencies
 */

import { speak } from '@wordpress/a11y';
import { getBlockType, doBlocksMatchTemplate, switchToBlockType, synchronizeBlocksWithTemplate, cloneBlock } from '@wordpress/blocks';
import { _n, sprintf } from '@wordpress/i18n';
import { create, toHTMLString, insert, remove } from '@wordpress/rich-text';
/**
 * Internal dependencies
 */

import { replaceBlocks, selectBlock, setTemplateValidity, resetBlocks, selectionChange } from './actions';
import { getBlock, getBlocks, getSelectedBlockCount, getTemplateLock, getTemplate, isValidTemplate, getSelectionStart } from './selectors';
/**
 * Block validity is a function of blocks state (at the point of a
 * reset) and the template setting. As a compromise to its placement
 * across distinct parts of state, it is implemented here as a side-
 * effect of the block reset action.
 *
 * @param {Object} action RESET_BLOCKS action.
 * @param {Object} store  Store instance.
 *
 * @return {?Object} New validity set action if validity has changed.
 */

export function validateBlocksToTemplate(action, store) {
  var state = store.getState();
  var template = getTemplate(state);
  var templateLock = getTemplateLock(state); // Unlocked templates are considered always valid because they act
  // as default values only.

  var isBlocksValidToTemplate = !template || templateLock !== 'all' || doBlocksMatchTemplate(action.blocks, template); // Update if validity has changed.

  if (isBlocksValidToTemplate !== isValidTemplate(state)) {
    return setTemplateValidity(isBlocksValidToTemplate);
  }
}
export default {
  MERGE_BLOCKS: function MERGE_BLOCKS(action, store) {
    var dispatch = store.dispatch;
    var state = store.getState();

    var _action$blocks = _slicedToArray(action.blocks, 2),
        clientIdA = _action$blocks[0],
        clientIdB = _action$blocks[1];

    var blockA = getBlock(state, clientIdA);
    var blockAType = getBlockType(blockA.name); // Only focus the previous block if it's not mergeable

    if (!blockAType.merge) {
      dispatch(selectBlock(blockA.clientId));
      return;
    }

    var blockB = getBlock(state, clientIdB);
    var blockBType = getBlockType(blockB.name);

    var _getSelectionStart = getSelectionStart(state),
        clientId = _getSelectionStart.clientId,
        attributeKey = _getSelectionStart.attributeKey,
        offset = _getSelectionStart.offset;

    var hasTextSelection = (clientId === clientIdA || clientId === clientIdB) && attributeKey !== undefined && offset !== undefined; // A robust way to retain selection position through various transforms
    // is to insert a special character at the position and then recover it.

    var START_OF_SELECTED_AREA = "\x86"; // Clone the blocks so we don't insert the character in a "live" block.

    var cloneA = cloneBlock(blockA);
    var cloneB = cloneBlock(blockB);

    if (hasTextSelection) {
      var selectedBlock = clientId === clientIdA ? cloneA : cloneB;
      var html = selectedBlock.attributes[attributeKey];
      var selectedBlockType = clientId === clientIdA ? blockAType : blockBType;
      var multilineTag = selectedBlockType.attributes[attributeKey].multiline;
      var value = insert(create({
        html: html,
        multilineTag: multilineTag
      }), START_OF_SELECTED_AREA, offset, offset);
      selectedBlock.attributes[attributeKey] = toHTMLString({
        value: value,
        multilineTag: multilineTag
      });
    } // We can only merge blocks with similar types
    // thus, we transform the block to merge first


    var blocksWithTheSameType = blockA.name === blockB.name ? [cloneB] : switchToBlockType(cloneB, blockA.name); // If the block types can not match, do nothing

    if (!blocksWithTheSameType || !blocksWithTheSameType.length) {
      return;
    } // Calling the merge to update the attributes and remove the block to be merged


    var updatedAttributes = blockAType.merge(cloneA.attributes, blocksWithTheSameType[0].attributes);

    if (hasTextSelection) {
      var newAttributeKey = findKey(updatedAttributes, function (v) {
        return typeof v === 'string' && v.indexOf(START_OF_SELECTED_AREA) !== -1;
      });
      var convertedHtml = updatedAttributes[newAttributeKey];
      var _multilineTag = blockAType.attributes[newAttributeKey].multiline;
      var convertedValue = create({
        html: convertedHtml,
        multilineTag: _multilineTag
      });
      var newOffset = convertedValue.text.indexOf(START_OF_SELECTED_AREA);
      var newValue = remove(convertedValue, newOffset, newOffset + 1);
      var newHtml = toHTMLString({
        value: newValue,
        multilineTag: _multilineTag
      });
      updatedAttributes[newAttributeKey] = newHtml;
      dispatch(selectionChange(blockA.clientId, newAttributeKey, newOffset, newOffset));
    }

    dispatch(replaceBlocks([blockA.clientId, blockB.clientId], [_objectSpread({}, blockA, {
      attributes: _objectSpread({}, blockA.attributes, updatedAttributes)
    })].concat(_toConsumableArray(blocksWithTheSameType.slice(1)))));
  },
  RESET_BLOCKS: [validateBlocksToTemplate],
  MULTI_SELECT: function MULTI_SELECT(action, _ref) {
    var getState = _ref.getState;
    var blockCount = getSelectedBlockCount(getState());
    /* translators: %s: number of selected blocks */

    speak(sprintf(_n('%s block selected.', '%s blocks selected.', blockCount), blockCount), 'assertive');
  },
  SYNCHRONIZE_TEMPLATE: function SYNCHRONIZE_TEMPLATE(action, _ref2) {
    var getState = _ref2.getState;
    var state = getState();
    var blocks = getBlocks(state);
    var template = getTemplate(state);
    var updatedBlockList = synchronizeBlocksWithTemplate(blocks, template);
    return resetBlocks(updatedBlockList);
  },
  MARK_AUTOMATIC_CHANGE: function MARK_AUTOMATIC_CHANGE(action, store) {
    var _window = window,
        setTimeout = _window.setTimeout,
        _window$requestIdleCa = _window.requestIdleCallback,
        requestIdleCallback = _window$requestIdleCa === void 0 ? function (callback) {
      return setTimeout(callback, 100);
    } : _window$requestIdleCa;
    requestIdleCallback(function () {
      store.dispatch({
        type: 'MARK_AUTOMATIC_CHANGE_FINAL'
      });
    });
  }
};
//# sourceMappingURL=effects.js.map